import DOMPurify from 'dompurify';
import { marked } from 'marked';

/**
 * 檢測內容是否為 markdown 格式
 * @param {string} content - 要檢測的內容
 * @returns {boolean} - 是否為 markdown 格式
 */
export const isMarkdownContent = (content) => {
    if (!content || typeof content !== 'string') return false;
    
    // 如果內容已經包含 HTML 標籤，且不是 markdown 語法，則視為 HTML
    const hasHtmlTags = /<[^>]+>/.test(content);
    const hasMarkdownSyntax = /^#{1,6}\s|^\*\s|^-\s|^\d+\.\s|^\*\*.*\*\*|^__.*__|^\*.*\*|^_.*_|^`.*`|^```|^\[.*\]\(.*\)|^!\[.*\]\(.*\)/m.test(content);
    
    // 如果同時有 HTML 標籤和 markdown 語法，優先判斷為 markdown
    if (hasMarkdownSyntax) return true;
    
    // 如果只有 HTML 標籤而沒有 markdown 語法，視為 HTML
    if (hasHtmlTags && !hasMarkdownSyntax) return false;
    
    // 如果都沒有，檢查是否為純文字（可能是 markdown）
    return !hasHtmlTags;
};

/**
 * 解析內容為 HTML，智能判斷是否為 markdown 格式
 * @param {string} content - 要解析的內容
 * @param {Object} options - 選項
 * @param {boolean} options.forceMarkdown - 強制使用 markdown 解析
 * @param {boolean} options.forceHtml - 強制使用 HTML 解析
 * @returns {string} - 解析後的 HTML 內容
 */
export const parseContentToHtml = (content, options = {}) => {
    if (!content || typeof content !== 'string') return '';
    
    const { forceMarkdown = false, forceHtml = false } = options;
    
    let shouldParseAsMarkdown = false;
    
    if (forceMarkdown) {
        shouldParseAsMarkdown = true;
    } else if (forceHtml) {
        shouldParseAsMarkdown = false;
    } else {
        // 智能判斷
        shouldParseAsMarkdown = isMarkdownContent(content);
    }
    
    let htmlContent;
    
    if (shouldParseAsMarkdown) {
        // 使用 marked 解析 markdown
        htmlContent = marked(content);
    } else {
        // 直接使用原始內容（假設已經是 HTML）
        htmlContent = content;
    }
    
    // 使用 DOMPurify 清理 HTML 內容，確保安全性
    // 配置 DOMPurify 允許表格等結構化元素和樣式屬性
    const cleanHtml = DOMPurify.sanitize(htmlContent, {
      ALLOWED_TAGS: [
        'p', 'br', 'strong', 'em', 'u', 's', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'ul', 'ol', 'li', 'blockquote', 'code', 'pre', 'a', 'img',
        'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'colgroup', 'col',
        'div', 'span', 'hr', 'sub', 'sup', 'del', 'ins'
      ],
      ALLOWED_ATTR: [
        'href', 'title', 'alt', 'src', 'width', 'height',
        'class', 'id', 'style',
        'colspan', 'rowspan', 'scope', 'align', 'valign',
        'cellpadding', 'cellspacing', 'border'
      ],
      ALLOWED_STYLES: {
        '*': {
          'color': [/^#[0-9a-fA-F]{3,6}$/, /^rgb/, /^rgba/],
          'text-align': [/^left$/, /^right$/, /^center$/, /^justify$/],
          'font-size': [/^\d+(?:px|em|rem|%)$/],
          'font-weight': [/^\d+$/, /^bold$/, /^normal$/],
          'text-decoration': [/^none$/, /^underline$/, /^line-through$/],
          'background-color': [/^#[0-9a-fA-F]{3,6}$/, /^rgb/, /^rgba/, /^transparent$/],
          'margin': [/^\d+(?:px|em|rem|%)$/],
          'padding': [/^\d+(?:px|em|rem|%)$/],
          'border': [/.*/],
          'width': [/^\d+(?:px|em|rem|%)$/],
          'height': [/^\d+(?:px|em|rem|%)$/]
        }
      },
      KEEP_CONTENT: true,
      RETURN_DOM: false,
      RETURN_DOM_FRAGMENT: false,
      RETURN_DOM_IMPORT: false,
      RETURN_TRUSTED_TYPE: false,
      FORCE_BODY: false,
      SANITIZE_DOM: true,
      ADD_ATTR: [],
      ADD_TAGS: []
    });
    
    return cleanHtml;
};

/**
 * 純 markdown 解析函數（向後兼容）
 * @param {string} content - 要解析的內容
 * @returns {string} - 解析後的 HTML 內容
 */
export const parseMarkdownContent = (content) => {
    return parseContentToHtml(content, { forceMarkdown: true });
};

/**
 * 純 HTML 解析函數
 * @param {string} content - 要解析的內容
 * @returns {string} - 清理後的 HTML 內容
 */
export const parseHtmlContent = (content) => {
    return parseContentToHtml(content, { forceHtml: true });
};
